import { Badge } from "@stockline/ui/components/badge";
import {
  MessageSquare,
  Mail,
  Phone,
  Upload,
  Play,
  MessageCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";

export type SourceType =
  | "whatsapp"
  | "email"
  | "sms"
  | "upload"
  | "manual"
  | "chat";

interface SourceBadgeProps {
  sourceType: SourceType;
  className?: string;
}

const sourceConfig: Record<
  SourceType,
  {
    icon: typeof MessageSquare;
    bgColor: string;
    textColor: string;
    labelKey: string;
  }
> = {
  whatsapp: {
    icon: MessageSquare,
    bgColor: "bg-green-100",
    textColor: "text-green-700",
    labelKey: "aiTransparency.source.whatsapp",
  },
  email: {
    icon: Mail,
    bgColor: "bg-blue-100",
    textColor: "text-blue-700",
    labelKey: "aiTransparency.source.email",
  },
  sms: {
    icon: Phone,
    bgColor: "bg-purple-100",
    textColor: "text-purple-700",
    labelKey: "aiTransparency.source.sms",
  },
  upload: {
    icon: Upload,
    bgColor: "bg-orange-100",
    textColor: "text-orange-700",
    labelKey: "aiTransparency.source.upload",
  },
  manual: {
    icon: Play,
    bgColor: "bg-gray-100",
    textColor: "text-gray-700",
    labelKey: "aiTransparency.source.manual",
  },
  chat: {
    icon: MessageCircle,
    bgColor: "bg-violet-100",
    textColor: "text-violet-700",
    labelKey: "aiTransparency.source.chat",
  },
};

/**
 * Extracts source type from execution context or agent_name
 */
export function extractSourceType(
  context?: Record<string, unknown> | null,
  agentName?: string | null,
): SourceType | null {
  // First check context for explicit source_type
  if (context?.source_type) {
    const type = context.source_type as string;
    if (type in sourceConfig) {
      return type as SourceType;
    }
  }

  // Then check agent_name for prefix pattern [SOURCE]
  if (agentName) {
    const match = agentName.match(/^\[(\w+)\]/i);
    if (match) {
      const prefix = match[1].toLowerCase();
      if (prefix in sourceConfig) {
        return prefix as SourceType;
      }
    }
  }

  return null;
}

export function SourceBadge({ sourceType, className = "" }: SourceBadgeProps) {
  const { t } = useTranslation();
  const config = sourceConfig[sourceType];
  const Icon = config.icon;

  return (
    <Badge
      variant="outline"
      className={`${config.bgColor} ${config.textColor} border-0 gap-1 ${className}`}
    >
      <Icon className="h-3 w-3" />
      <span className="text-xs font-medium">
        {t(config.labelKey, { defaultValue: sourceType.toUpperCase() })}
      </span>
    </Badge>
  );
}
